import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { TextField, InputAdornment } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Tooltip from 'components/Tooltip';

const TextInput = props => {
  const {
    disabled, error, errorPlacement, hideRequiredText, hideTooltip, InputProps, inputProps, maxLength, maxRows,
    multiline, placeholder, rows, type, style, readOnly, required, value, onBlur, onChange, onFocus, onKeyDown,
    highlightMissing,
  } = props;
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const [requiredVisible, setRequiredVisible] = useState(false);
  const [errorVisible, setErrorVisible] = useState(false);
  const [borderColor, setBorderColor] = useState('');
  const [backgroundColor, setBackgroundColor] = useState(theme.palette.background.default);
  const isInitialValueSet = useRef(false);

  // Highlight the border in red if the UI focus is no longer on the input field and its value is invalid
  const handleBlurred = event => {
    // If there's an error, display it in a tooltip
    setErrorVisible(true);
    if (onBlur) {
      onBlur(event);
    }
  };

  useEffect(() => {
    // Check the default input value and if there's an error, display it in a tool tip
    if ((value && value?.length > 0) && !isInitialValueSet.current) {
      setErrorVisible(true);
      isInitialValueSet.current = true;
    }
  }, [value]);

  // Highlight the border and background if it is required and has no value set
  useEffect(() => {
    const missingRequired = required && highlightMissing && !value;
    setBorderColor(missingRequired || (errorVisible && error) ? theme.palette.error.main : '');
    setBackgroundColor(missingRequired ? theme.palette.error.light : '');
    setRequiredVisible(missingRequired);
    // We can safely assume the [theme] will be static, so don't update the sate if theme.palette.error.main or light change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, errorVisible, required, value, highlightMissing]);

  const handleFocus = event => {
    // Hide the error tooltip when the focus is on the input
    setErrorVisible(false);
    if (typeof onFocus === 'function') {
      onFocus(event);
    }
  };

  const handleKeyDown = event => {
    if (typeof onKeyDown === 'function') {
      onKeyDown(event);
    }
  };

  // The Input value has changed, validate it and call the onChange handler if allowed
  const handleChange = event => {
    if (typeof onChange === 'function') {
      onChange(event);
    }
  };

  return (
    <Tooltip
      open={!hideTooltip && errorVisible && !!error}
      placement={errorPlacement}
      title={error}
      type="error"
    >
      <TextField
        fullWidth
        error={!!borderColor}
        InputProps={{
          // InputProps is expected to be set by the Autocomplete component
          ...InputProps,
          // Apply background color to the entire input area
          style: {
            backgroundColor: backgroundColor !== theme.palette.background.default ? backgroundColor : undefined,
            ...(InputProps?.style || {}),
          },
          // Override the fieldset border when there's an error state
          sx: {
            ...(borderColor && {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${borderColor} !important`,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: `${borderColor} !important`,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: `${borderColor} !important`,
                borderWidth: '1px !important',
              },
            }),
            ...(InputProps?.sx || {}),
          },
          // Render "required" adornment if the input value is required but empty
          // Suppressed on inputs too narrow to hold the word — the field's
          // own label still carries the required marker.
          endAdornment:
            requiredVisible && !hideRequiredText ? (
              <InputAdornment disableTypography position="end">
                {formatMessage({ id: 'validation.required' }).toLowerCase()}
              </InputAdornment>
            ) : null,
          // Props for the HTML Input element itself
          inputProps: {
            // inputProps is expected to be set by the Autocomplete component
            ...inputProps,
            style: { backgroundColor, ...style },
            maxLength,
            readOnly,
            onKeyDown: handleKeyDown,
          },
          // Handle the event when the focus on the input element is lost
          onBlur: handleBlurred,
        }}
        disabled={disabled}
        maxRows={maxRows}
        multiline={multiline}
        placeholder={placeholder}
        readOnly={readOnly}
        required={required}
        rows={rows}
        type={type}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
      />
    </Tooltip>
  );
};

TextInput.propTypes = {
  disabled: PropTypes.bool,
  // Error to be rendered in a tooltip when a validation by validateValue fails.
  error: PropTypes.string,
  errorPlacement: PropTypes.string,
  hideRequiredText: PropTypes.bool,
  hideTooltip: PropTypes.bool,
  maxLength: PropTypes.number,
  maxRows: PropTypes.number,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  type: PropTypes.string,
  // The Input Props are required for the Autocomplete component to be able to render the options list
  InputProps: PropTypes.object,
  inputProps: PropTypes.object,
  placeholder: PropTypes.string,
  style: PropTypes.object,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  value: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  highlightMissing: PropTypes.bool,
};

TextInput.defaultProps = {
  disabled: false,
  errorPlacement: 'left',
  hideTooltip: false,
  style: {},
  InputProps: {},
  inputProps: {},
  highlightMissing: true,
};

export default TextInput;
